import React, { useEffect, useMemo, useState } from "react";
import {
  FiExternalLink,
  FiFileText,
  FiFolder,
  FiInfo,
  FiLink,
  FiSave,
  FiStar,
  FiX,
} from "react-icons/fi";

import { getAllPages } from "../../../../services/pagesServices";
import Drawer from "./Drawer";

import {
  flattenMenuTree,
  getDescendantIds,
  getMenuItemType,
  getTypeLabel,
} from "../utils/menuAdminUtils";

const inputClass =
  "w-full rounded-2xl border border-slate-300 bg-white px-4 py-2.5 text-sm text-slate-900 outline-none transition focus:border-sky-500 focus:ring-2 focus:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:ring-sky-950";


export default function MenuItemForm({
  open,
  onClose,
  onSubmit,
  initial = {},
  menuKey = "header",
  locale = "fr",
  allItemsFlat = [],
  allItemsTree = [],
}) {
  const isEdit = !!initial?.id;

  const [pages, setPages] = useState([]);
  const [pagesLoading, setPagesLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [form, setForm] = useState({
    label: "",
    item_type: "folder",
    title_attr: "",
    url: "",
    page_id: null,
    target: "_self",
    rel: "",
    icon: "",
    parent_id: null,
    order_index: 0,
    is_active: true,
    is_favorite: false,
  });

  useEffect(() => {
    if (!open) return;

    let mounted = true;

    const fetchPages = async () => {
      try {
        setPagesLoading(true);

        const data = await getAllPages();

        if (mounted) {
          setPages(Array.isArray(data) ? data : []);
        }
      } catch (error) {
        console.error("Erreur chargement pages:", error);
        if (mounted) setPages([]);
      } finally {
        if (mounted) setPagesLoading(false);
      }
    };

    fetchPages();

    return () => {
      mounted = false;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const type = getInitialType(initial);

    setError("");

    setForm({
      label: initial.label || "",
      item_type: type,
      title_attr: initial.title_attr || "",
      url: initial.url || "",
      page_id: initial.page?.id || initial.page_id || null,
      target: initial.target || "_self",
      rel: initial.rel || "",
      icon: initial.icon || "",
      parent_id: initial.parent_id ?? null,
      order_index: initial.order_index ?? 0,
      is_active: initial.is_active ?? true,
      is_favorite: initial.is_favorite ?? false,
    });
  }, [open, initial]);

  const parentOptions = useMemo(() => {
    const sourceFlat = allItemsFlat?.length
      ? allItemsFlat
      : flattenMenuTree(allItemsTree || []);

    const descendants = initial?.id
      ? getDescendantIds(allItemsTree || [], initial.id)
      : new Set();

    const allowed = sourceFlat.filter((item) => {
      if (item.id === initial?.id) return false;
      if (descendants.has(item.id)) return false;

      return true;
    });

    return [
      {
        id: null,
        label: "Racine du menu",
        helper: "devient un onglet principal",
        depth: 0,
      },
      ...allowed.map((item) => ({
        ...item,
        helper:
          item.type === "folder"
            ? "sera placé dans ce dossier"
            : "attention : ce parent est plutôt une page/lien",
      })),
    ];
  }, [allItemsFlat, allItemsTree, initial?.id]);

  const selectedParent = useMemo(() => {
    if (!form.parent_id) return null;

    return parentOptions.find((item) => item.id === form.parent_id) || null;
  }, [form.parent_id, parentOptions]);

  const linkPreview = useMemo(() => {
    if (form.item_type === "folder") {
      return "Aucun lien : ce dossier contient des éléments.";
    }

    if (form.item_type === "page") {
      const page = pages.find((item) => item.id === form.page_id);

      if (!page) return "Sélectionnez une page du site.";

      return page.slug ? `/pages/${page.slug}` : "Page interne sélectionnée";
    }

    return form.url?.trim() || "Renseignez une URL.";
  }, [form.item_type, form.page_id, form.url, pages]);

  const updateField = (key, value) => {
    setForm((current) => ({
      ...current,
      [key]: value,
    }));
  };

  const selectType = (type) => {
    setForm((current) => ({
      ...current,
      item_type: type,
      page_id: type === "page" ? current.page_id : null,
      url: type === "url" ? current.url : "",
      target: type === "folder" ? "_self" : current.target || "_self",
      rel: type === "folder" ? "" : current.rel || "",
    }));
  };

  const validate = () => {
    if (!form.label.trim()) return "Le nom affiché est obligatoire.";

    if (form.item_type === "page" && !form.page_id) {
      return "Sélectionnez une page du site.";
    }

    if (form.item_type === "url" && !form.url.trim()) {
      return "Renseignez l’URL du lien.";
    }

    return "";
  };

  const buildPayload = () => {
    const target = form.target || "_self";

    return {
      menu_key: menuKey,
      locale,
      label: form.label.trim(),
      link_type:
        form.item_type === "folder"
          ? "none"
          : form.item_type === "page"
            ? "page"
            : "url",

      title_attr: form.title_attr?.trim() || null,

      url: form.item_type === "url" ? form.url.trim() : null,
      page_id: form.item_type === "page" ? form.page_id || null : null,

      target,

      // Champ technique généré automatiquement
      rel: target === "_blank" ? "noopener noreferrer" : null,

      // On garde la valeur existante si jamais tu l’utilises plus tard,
      // mais on ne l’affiche plus au client.
      icon: form.icon?.trim() || null,

      parent_id: form.parent_id ?? null,
      order_index: Number(form.order_index) || 0,
      is_active: !!form.is_active,
      is_favorite: !!form.is_favorite,
    };
  };

  const submit = async () => {
    const validationError = validate();

    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setSaving(true);
      setError("");

      await onSubmit(buildPayload());
    } catch (submitError) {
      console.error("Erreur MenuItemForm submit:", submitError);
      setError("Erreur lors de l’enregistrement.");
    } finally {
      setSaving(false);
    }
  };

  const footer = (
    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
      <button
        type="button"
        onClick={onClose}
        disabled={saving}
        className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800"
      >
        <FiX />
        Annuler
      </button>

      <button
        type="button"
        onClick={submit}
        disabled={saving}
        className="inline-flex items-center justify-center gap-2 rounded-2xl bg-sky-600 px-5 py-3 text-sm font-bold text-white shadow-sm transition hover:bg-sky-700 disabled:opacity-50"
      >
        <FiSave />
        {saving ? "Enregistrement…" : isEdit ? "Enregistrer" : "Créer"}
      </button>
    </div>
  );

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={isEdit ? "Modifier un élément" : "Créer un élément"}
      subtitle="Choisissez s’il s’agit d’un dossier, d’une page du site ou d’un lien personnalisé."
      size="lg"
      footer={footer}
    >
      <div className="space-y-6">
        {error ? (
          <div className="rounded-3xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-medium text-rose-700 dark:border-rose-900/50 dark:bg-rose-950/30 dark:text-rose-200">
            {error}
          </div>
        ) : null}

        <section className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <SectionTitle
            number="1"
            title="Type d’élément"
            description="Un dossier sert à ranger. Une page ou un lien est cliquable."
          />

          <div className="mt-5 grid gap-3 md:grid-cols-3">
            <TypeCard
              active={form.item_type === "folder"}
              icon={<FiFolder />}
              title="Dossier"
              text="Contient des pages ou d’autres dossiers."
              onClick={() => selectType("folder")}
            />

            <TypeCard
              active={form.item_type === "page"}
              icon={<FiFileText />}
              title="Page du site"
              text="Mène vers une page créée dans le CMS."
              onClick={() => selectType("page")}
            />

            <TypeCard
              active={form.item_type === "url"}
              icon={<FiLink />}
              title="Lien personnalisé"
              text="Mène vers une URL interne ou externe."
              onClick={() => selectType("url")}
            />
          </div>
        </section>

        <section className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <SectionTitle
            number="2"
            title="Nom affiché"
            description="C’est le texte visible dans le menu."
          />

          <div className="mt-5">
            <Field label="Nom affiché *">
              <input
                value={form.label}
                onChange={(e) => updateField("label", e.target.value)}
                className={inputClass}
                placeholder="Ex : Vie municipale, Actualités, Contact..."
              />
            </Field>
          </div>

          <div className="mt-4">
            <Field label="Titre au survol">
              <input
                value={form.title_attr}
                onChange={(e) => updateField("title_attr", e.target.value)}
                className={inputClass}
                placeholder="Optionnel"
              />
            </Field>
          </div>
        </section>

        <section className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <SectionTitle
            number="3"
            title="Destination"
            description="Uniquement pour les pages et les liens. Un dossier n’a pas de destination."
          />

          <div className="mt-5">
            {form.item_type === "folder" ? (
              <div className="rounded-3xl border border-violet-200 bg-violet-50 p-4 text-sm leading-6 text-violet-800 dark:border-violet-900/50 dark:bg-violet-950/30 dark:text-violet-200">
                <div className="flex items-start gap-3">
                  <FiInfo className="mt-1 shrink-0" />
                  <div>
                    Cet élément est un <strong>dossier</strong>. Il ne mène pas
                    vers une page : il sert à contenir d’autres éléments.
                  </div>
                </div>
              </div>
            ) : form.item_type === "page" ? (
              <Field label="Page du site *">
                <select
                  value={form.page_id ?? ""}
                  onChange={(e) =>
                    updateField(
                      "page_id",
                      e.target.value ? Number(e.target.value) : null,
                    )
                  }
                  className={inputClass}
                >
                  <option value="">
                    {pagesLoading
                      ? "Chargement des pages…"
                      : "Sélectionner une page"}
                  </option>

                  {pages.map((page) => (
                    <option key={page.id} value={page.id}>
                      {page.title} {page.slug ? `(/pages/${page.slug})` : ""}
                    </option>
                  ))}
                </select>
              </Field>
            ) : (
              <Field label="URL *">
                <div className="relative">
                  <FiExternalLink className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />

                  <input
                    value={form.url}
                    onChange={(e) => updateField("url", e.target.value)}
                    className={`${inputClass} pl-11`}
                    placeholder="/contact ou https://..."
                  />
                </div>
              </Field>
            )}
          </div>

          <div className="mt-4 rounded-2xl bg-slate-50 px-4 py-3 text-xs text-slate-500 dark:bg-slate-950 dark:text-slate-400">
            Aperçu :{" "}
            <strong className="text-slate-700 dark:text-slate-200">
              {linkPreview}
            </strong>
          </div>
        </section>

        <section className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <SectionTitle
            number="4"
            title="Emplacement"
            description="À la racine = onglet principal. Dans un dossier = sous-menu."
          />

          <div className="mt-5">
            <Field label="Placer cet élément dans">
              <select
                value={form.parent_id ?? ""}
                onChange={(e) =>
                  updateField(
                    "parent_id",
                    e.target.value === "" ? null : Number(e.target.value),
                  )
                }
                className={inputClass}
              >
                {parentOptions.map((option) => (
                  <option key={String(option.id)} value={option.id ?? ""}>
                    {option.id === null
                      ? "Racine du menu — onglet principal"
                      : `${"— ".repeat(option.depth || 0)}${option.label} (${
                          option.type ? getTypeLabel(option.type) : "Élément"
                        })`}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          <div className="mt-4 rounded-2xl bg-slate-50 px-4 py-3 text-sm leading-6 text-slate-600 dark:bg-slate-950 dark:text-slate-300">
            {selectedParent ? (
              <>
                L’élément sera placé dans{" "}
                <strong>{selectedParent.label}</strong>.
              </>
            ) : (
              <>
                L’élément sera placé à la racine : il deviendra un{" "}
                <strong>onglet principal</strong>.
              </>
            )}
          </div>

          {selectedParent && selectedParent.type !== "folder" ? (
            <div className="mt-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm leading-6 text-amber-800 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-200">
              Attention : le parent choisi est plutôt une page ou un lien. Pour
              garder une organisation claire, il vaut mieux placer les éléments
              dans un dossier.
            </div>
          ) : null}
        </section>

        <section className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <SectionTitle
            number="5"
            title="Options d’affichage"
            description="Choisissez simplement si l’élément est visible et comment le lien doit s’ouvrir."
          />

          <div className="mt-5 grid gap-4 md:grid-cols-2">
            <label className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-medium text-slate-700 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-200">
              <input
                type="checkbox"
                checked={!!form.is_active}
                onChange={(e) => updateField("is_active", e.target.checked)}
                className="mt-1 rounded border-slate-300"
              />

              <span>
                <strong>Afficher dans le menu</strong>
                <span className="mt-1 block text-xs font-normal text-slate-500 dark:text-slate-400">
                  Décochez pour masquer temporairement cet élément sans le
                  supprimer.
                </span>
              </span>
            </label>

            {menuKey === "header" ? (
              <label className="flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-medium text-amber-800 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-200">
                <input
                  type="checkbox"
                  checked={!!form.is_favorite}
                  onChange={(e) => updateField("is_favorite", e.target.checked)}
                  className="mt-1 rounded border-slate-300"
                />

                <span>
                  <strong className="inline-flex items-center gap-2">
                    <FiStar />
                    Afficher en raccourci
                  </strong>

                  <span className="mt-1 block text-xs font-normal opacity-80">
                    L’élément apparaîtra aussi dans la zone des raccourcis du
                    header.
                  </span>
                </span>
              </label>
            ) : null}
          </div>

          {form.item_type !== "folder" ? (
            <div className="mt-5">
              <Field label="Ouverture du lien">
                <select
                  value={form.target}
                  onChange={(e) => updateField("target", e.target.value)}
                  className={inputClass}
                >
                  <option value="_self">Dans le même onglet</option>
                  <option value="_blank">Dans un nouvel onglet</option>
                </select>
              </Field>

              <p className="mt-2 text-xs leading-5 text-slate-500 dark:text-slate-400">
                Pour les liens externes, il est souvent préférable d’ouvrir dans
                un nouvel onglet. Les réglages techniques sont gérés
                automatiquement.
              </p>
            </div>
          ) : (
            <div className="mt-5 rounded-2xl bg-slate-50 px-4 py-3 text-sm leading-6 text-slate-600 dark:bg-slate-950 dark:text-slate-300">
              Cet élément est un dossier : il sert uniquement à organiser les
              pages et n’a pas besoin d’option d’ouverture.
            </div>
          )}
        </section>
      </div>
    </Drawer>
  );
}

function getInitialType(initial = {}) {
  if (initial.item_type) return initial.item_type;

  const detected = getMenuItemType(initial);

  if (detected === "url") return "url";
  if (detected === "page") return "page";

  return "folder";
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
        {label}
      </span>
      {children}
    </label>
  );
}

function SectionTitle({ number, title, description }) {
  return (
    <div className="flex items-start gap-3">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-slate-950 text-sm font-bold text-white dark:bg-slate-100 dark:text-slate-950">
        {number}
      </div>

      <div>
        <h3 className="font-bold text-slate-950 dark:text-slate-50">{title}</h3>

        {description ? (
          <p className="mt-1 text-sm leading-6 text-slate-500 dark:text-slate-400">
            {description}
          </p>
        ) : null}
      </div>
    </div>
  );
}

function TypeCard({ active, icon, title, text, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-3xl border p-4 text-left transition ${
        active
          ? "border-sky-400 bg-sky-50 ring-2 ring-sky-100 dark:border-sky-700 dark:bg-sky-950/30 dark:ring-sky-950"
          : "border-slate-200 bg-white hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-950 dark:hover:bg-slate-800"
      }`}
    >
      <div
        className={`flex h-11 w-11 items-center justify-center rounded-2xl ${
          active
            ? "bg-sky-600 text-white"
            : "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-300"
        }`}
      >
        {icon}
      </div>

      <div className="mt-3 font-bold">{title}</div>

      <p className="mt-1 text-sm leading-6 text-slate-500 dark:text-slate-400">
        {text}
      </p>
    </button>
  );
}
